import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { DateTime } from "luxon";
import { UIChart } from "primeng/chart";
import { DashboardWidget } from "src/app/shared/model/dashboard-config.model";
import { CopyToClipboardService } from "src/app/shared/services/copy-to-clipboard.service";
import { WidgetsService } from "src/app/shared/services/widgets.service";

@Component({
  selector: "widget-stage-counters",
  templateUrl: "./widget-stage-counters.component.html",
  styleUrls: ["./widget-stage-counters.component.scss"],
})
export class WidgetStageCountersComponent implements OnInit {
  @ViewChild("doughnutChart") doughnutChart: UIChart;
  @ViewChild("doughnutChart2") doughnutChart2: UIChart;
  @ViewChild("doughnutChart3") doughnutChart3: UIChart;
  @Input() widgetConfig: DashboardWidget;
  widgetSize: number = undefined;
  loading = false;
  failedToLoad: boolean = false;

  nowISO: string = DateTime.fromJSDate(new Date()).setZone("UTC").toISO();
  stageStarted: boolean = false;
  
  total = 0;
  totalWording: string = '';
  totalTooltip: string = '';
  total2 = 0;
  totalWording2: string = '';
  totalTooltip2: string = '';
  total3 = 0;
  totalWording3: string = '';
  totalTooltip3: string = '';

  stageTypeName: string = '';
  stageData: any = null;
  stageName: string = '';

  doughnutDataHasValue: boolean = false;
  doughnutData = {
    labels: [],
    datasets: [],
  };

  doughnutData2HasValue: boolean = false;
  doughnutData2 = {
    labels: [],
    datasets: [],
  };

  doughnutData3HasValue: boolean = false;
  doughnutData3 = {
    labels: [],
    datasets: [],
  };

  doughnutOptions = {
    cutout: 50,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        labels: {
          name: {
            align: 'top',
            font: {family: 'Europa', size: 18},
            color: function(ctx) {
              return [0, 8, 9].includes(ctx.dataIndex) ? "#ffffff" : "#000000";
            },
            formatter: function(value, ctx) {
              if (value) {
                return value;
              } else {
                return '';
              };
            }
          },
          value: {
            align: 'center',
            font: {family: 'Europa', size: 12},
            color: function(ctx) {
              return [0, 8, 9].includes(ctx.dataIndex) ? "#ffffff" : "#000000";
            },
            formatter: function(value, ctx) {
              if (value) {
                return ctx.chart.data.labels[ctx.dataIndex].toUpperCase();
              } else {
                return '';
              };
            },
            padding: 1
          }
        },
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        callbacks: {
          label: (context) => {
            return context.label + ': ' + context.formattedValue;
          },
        },
      },
    },
  };

  doughnutPlugins = [];

  colors = [
    "#4D71FE",
    "#8BA3FE",
    "#C1CDFF",
    "#E4EAFF",
    "#F1993B",
    "#F5C087",
    "#FADBBB",
    "#FDF0E2",
    "#39289F",
    "#9289CA",
  ];

  constructor(
    private widgetsService: WidgetsService,
    private copyToClipboardService: CopyToClipboardService
  ) {}

  ngOnInit() {
    this.widgetSize = this.widgetConfig.widgetHorizontalPercentageSize;
    this.init();
  }

  async init() {

    if (this.widgetConfig.widgetSettings.stageId) {
      // load data
      this.loading = true;
      try {
        const res: any = await this.widgetsService.getStageCountersWidgetData([this.widgetConfig.widgetSettings.stageId]).toPromise();
        this.stageData = res.results[0];
        this.stageTypeName = this.stageData.stageTypeName;
        this.stageName = this.stageData.stageNameForDisplay;

        const stageStartDate = DateTime.fromISO(this.stageData.startDate, {zone: 'UTC'});
        this.stageStarted = stageStartDate.diff(DateTime.fromISO(this.nowISO), 'seconds').toObject().seconds > 0 ? false : true;

        if (this.stageTypeName === 'Registration Form' || this.stageTypeName === 'Application Form' || this.stageTypeName === 'Form') {
          // form stages
          const labels = ['Submitted', 'In progress'];
          const datasets = [
            {
              label: 'Values',
              data: [this.stageData.submittedCount, this.stageData.inProgressCount],
              backgroundColor: [...this.colors],
              hoverOffset: 4,
            }
          ]
          // check for if the data has value or only 0
          // used in showing/hiding the empty state
          if (this.stageData.submittedCount > 0 || this.stageData.inProgressCount > 0) {
            this.doughnutDataHasValue = true;
          };
          this.total = this.stageData.totalCount;
          this.totalWording = 'Total';
          this.totalTooltip = 'Total participants assigned to this Form stage';
          this.doughnutData.labels = labels;
          this.doughnutData.datasets = datasets;
          this.doughnutChart?.refresh();
        } else if (this.stageTypeName === 'Admin Review') {
          // admin review
          const labels = ['Valid', 'Invalid', 'Unfinished'];
          const datasets = [
            {
              label: 'Values',
              data: [this.stageData.valid, this.stageData.invalid, this.stageData.unfinished],
              backgroundColor: [...this.colors],
              hoverOffset: 4,
            }
          ]
          // check for if the data has value or only 0
          // used in showing/hiding the empty state
          if (this.stageData.valid > 0 || this.stageData.invalid > 0 || this.stageData.unfinished > 0) {
            this.doughnutDataHasValue = true;
          };
          this.total = this.stageData.total;
          this.totalWording = 'Total';
          this.totalTooltip = 'Total reviews assigned to this AR stage';
          this.doughnutData.labels = labels;
          this.doughnutData.datasets = datasets;
          this.doughnutChart?.refresh();

          const labels2 = ['Final', 'Not Final'];
          const datasets2 = [
            {
              label: 'Values',
              data: [this.stageData.final, (this.stageData.notFinal - this.stageData.unfinished)],
              backgroundColor: [...this.colors],
              hoverOffset: 4,
            }
          ]
          // check for if the data has value or only 0
          // used in showing/hiding the empty state
          if (this.stageData.final > 0 || this.stageData.notFinal > 0 || this.stageData.unfinished > 0) {
            this.doughnutData2HasValue = true;
          };
          // the total valid or invalid are all finished reviews
          // they are either final or not final
          this.total2 = this.stageData.totalValidOrInvalid;
          this.totalWording2 = 'Total';
          this.totalTooltip2 = 'Total Final & Not Final finished reviews'
          this.doughnutData2.labels = labels2;
          this.doughnutData2.datasets = datasets2;
          this.doughnutChart2?.refresh();
        } else if (this.stageTypeName === 'Bulk Review') {
          // bulk review
          const labels = ['Valid', 'Invalid', 'Open'];
          const datasets = [
            {
              label: 'Values',
              data: [this.stageData.valid, this.stageData.invalid, this.stageData.open],
              backgroundColor: [...this.colors],
              hoverOffset: 4,
            }
          ]
          // check for if the data has value or only 0
          // used in showing/hiding the empty state
          if (this.stageData.valid > 0 || this.stageData.invalid > 0 || this.stageData.open > 0) {
            this.doughnutDataHasValue = true;
          };
          this.total = this.stageData.valid + this.stageData.invalid + this.stageData.open;
          this.totalWording = 'Total';
          this.totalTooltip = 'Total reviews assigned to this BR stage'
          this.doughnutData.labels = labels;
          this.doughnutData.datasets = datasets;
          this.doughnutChart?.refresh();
        } else if (this.stageTypeName === 'Peer Review') {
          // peer review
          const labels = ['Submitted', 'In Progress', 'Not Started',];
          const datasets = [
            {
              label: 'Values',
              data: [this.stageData.requiredSubmitted, this.stageData.requiredInProgress, this.stageData.requiredNotStarted],
              backgroundColor: [...this.colors],
              hoverOffset: 4,
            }
          ]
          // check for if the data has value or only 0
          // used in showing/hiding the empty state
          if (this.stageData.requiredSubmitted > 0 || this.stageData.requiredInProgress > 0 || this.stageData.requiredNotStarted > 0) {
            this.doughnutData3HasValue = true;
          };
          this.total3 = this.stageData.reviewsNeeded;
          this.totalWording3 = 'Needed';
          this.totalTooltip3 = 'Total needed (required) reviews';
          this.doughnutData3.labels = labels;
          this.doughnutData3.datasets = datasets;
          this.doughnutChart3?.refresh();
        } else if (this.stageTypeName === 'Evaluation') {
          // evaluation
          const labels = ['Submitted', 'In Progress', 'Not Started'];
          const datasets = [
            {
              label: 'Values',
              data: [this.stageData.requiredSubmitted, this.stageData.requiredInProgress, this.stageData.requiredNotStarted],
              backgroundColor: [...this.colors],
              hoverOffset: 4,
            }
          ]
          // check for if the data has value or only 0
          // used in showing/hiding the empty state
          if (this.stageData.requiredSubmitted > 0 || this.stageData.requiredInProgress > 0 || this.stageData.requiredNotStarted > 0) {
            this.doughnutData3HasValue = true;
          };
          this.total3 = this.stageData.reviewsNeeded;
          this.totalWording3 = 'Needed';
          this.totalTooltip3 = 'Total needed (required) reviews';
          this.doughnutData3.labels = labels;
          this.doughnutData3.datasets = datasets;
          this.doughnutChart3?.refresh();
        }
        this.loading = false;
        this.failedToLoad = false;
      } catch (error) {
        this.loading = false;
        this.failedToLoad = true;
        console.log(error);
      }
    } else {
      // no stage id available
    }
  }

  reloadWidget() {
    this.init();
  }

  clickToCopy(stringToCopy: string) {
    this.copyToClipboardService.copyToClipboard(stringToCopy);
  }
}
